import * as React from 'react';
import BaseSelect, { SelectOption } from 'components/inputs/BaseSelect.func';
import { MenuPlacement } from 'react-select';
import BaseInput from 'components/inputs/BaseInput';
import { ActionMeta, ValueType } from 'react-select/src/types';
import noop from 'lodash';

type Props<T extends string | number | null> = {
  id: string;
  className?: string;
  options: SelectOption<T>[];
  value?: SelectOption<T> | null;
  menuPlacement?: MenuPlacement;
  onChange: (newSelectedOption: SelectOption<T>) => void;
  onClear?: (removedValues: SelectOption<T>[]) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  isClearable?: boolean;
};

function SingleSelect<T extends string | number | null = string | null>(
  props: Props<T>
) {
  const {
    disabled = false,
    value = null,
    options = [],
    menuPlacement = 'auto',
    onChange = noop,
    ...rest
  } = props;

  const handleChange = React.useCallback(
    (
      selectedOption: ValueType<SelectOption<T>, boolean>,
      actionData: ActionMeta<SelectOption<T>>
    ) => {
      switch (actionData.action) {
        case 'select-option':
          onChange(selectedOption as SelectOption<T>);
          break;
        case 'clear':
          onChange({ value: null, label: '' } as SelectOption<T>);
          break;
        default:
          onChange(selectedOption as SelectOption<T>);
          break;
      }
    },
    [onChange]
  );

  if (disabled) {
    return (
      <BaseInput
        id={`${value ? value.label : ''}-disabled-input`}
        type="text"
        value={value ? value.label : ''}
        name={value ? value.label : ''}
        disabled
      />
    );
  }

  return (
    <BaseSelect<T>
      {...rest}
      menuPlacement={menuPlacement}
      value={value}
      options={options}
      onChange={handleChange}
    />
  );
}

SingleSelect.displayName = 'SingleSelectFunc';

export default SingleSelect;
